import React, { useRef, useEffect } from 'react';
import { stripHtml } from '../../utils/sanitizeHtml';
import { useAuth } from '../../context/AuthContext';
import { usePlanPermissions, getCurrentPlan } from '../../utils/planPermissions';

const SuggestionDropdown = ({ open, items, onSelect, anchorRect, activeIndex, onActiveIndexChange }) => {
  const activeItemRef = useRef(null);
  const listRef = useRef(null);
  const dropdownRef = useRef(null);
  const [activeTab, setActiveTab] = React.useState('content');
  
  // Permissions basées sur le plan
  const { user, activeTenantId } = useAuth();
  const currentPlan = getCurrentPlan(user, activeTenantId);
  const { canMentionUsers, canMentionTodos, canMentionMessages } = usePlanPermissions(currentPlan);

  // Basculer sur l'onglet approprié selon les permissions
  useEffect(() => {
    if (activeTab === 'users' && !canMentionUsers) {
      if (canMentionMessages || canMentionTodos) {
        setActiveTab('content');
        onActiveIndexChange && onActiveIndexChange(0);
      }
    } else if (activeTab === 'content' && !canMentionMessages && !canMentionTodos && canMentionUsers) {
      setActiveTab('users');
      onActiveIndexChange && onActiveIndexChange(0);
    }
  }, [canMentionUsers, canMentionMessages, canMentionTodos, activeTab, onActiveIndexChange]);

  // utiliser le util stripHtml importé

  // Scroll automatique vers l'item actif
  useEffect(() => {
    if (activeItemRef.current && listRef.current) {
      const itemTop = activeItemRef.current.offsetTop;
      const itemBottom = itemTop + activeItemRef.current.offsetHeight;
      const listTop = listRef.current.scrollTop;
      const listBottom = listTop + listRef.current.offsetHeight;

      if (itemTop < listTop) {
        listRef.current.scrollTop = itemTop;
      } else if (itemBottom > listBottom) {
        listRef.current.scrollTop = itemBottom - listRef.current.offsetHeight;
      }
    }
  }, [activeIndex]);

  // Gérer les événements clavier dans la modale
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (['Tab', 'ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
      }

      const contentItems = items.filter(it => 
        (it.kind === 'msg' && canMentionMessages) || 
        (it.kind === 'todo' && canMentionTodos)
      );
      const userItems = canMentionUsers ? items.filter(it => it.kind === 'user') : [];
      const displayItems = activeTab === 'content' ? contentItems : userItems;

      if (e.key === 'Tab' && canMentionUsers && (canMentionMessages || canMentionTodos)) {
        setActiveTab(prev => prev === 'content' ? 'users' : 'content');
        onActiveIndexChange && onActiveIndexChange(0);
        return;
      }

      if (e.key === 'ArrowDown') {
        onActiveIndexChange && onActiveIndexChange((activeIndex + 1) % displayItems.length);
        return;
      }

      if (e.key === 'ArrowUp') {
        onActiveIndexChange && onActiveIndexChange((activeIndex - 1 + displayItems.length) % displayItems.length);
        return;
      }

      if (e.key === 'Enter') {
        if (displayItems[activeIndex]) {
          onSelect(displayItems[activeIndex]);
        }
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, activeIndex, activeTab, items, onActiveIndexChange, onSelect, canMentionUsers, canMentionMessages, canMentionTodos]);

  useEffect(() => {
    if (open && dropdownRef.current) {
      dropdownRef.current.focus();
    }
  }, [open]);

  if (!open || !items || items.length === 0) return null;
  
  // Ne pas afficher si aucune mention n'est autorisée
  if (!canMentionMessages && !canMentionTodos && !canMentionUsers) return null;

  const contentItems = items.filter(it => 
    (it.kind === 'msg' && canMentionMessages) || 
    (it.kind === 'todo' && canMentionTodos)
  );
  const userItems = canMentionUsers ? items.filter(it => it.kind === 'user') : [];
  const displayItems = activeTab === 'content' ? contentItems : userItems;

  const style = anchorRect
    ? (() => {
      const minW = 300;
      const maxW = 560;
      const width = Math.min(Math.max(minW, anchorRect.width), maxW);
      const margin = 8;

      const spaceBelow = window.innerHeight - anchorRect.bottom;
      const spaceAbove = anchorRect.top;
      const dropdownHeight = Math.min(350, displayItems.length * 70 + 150);

      const showAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      const top = showAbove
        ? Math.max(margin, anchorRect.top - dropdownHeight - 6)
        : Math.min(window.innerHeight - margin - dropdownHeight, anchorRect.bottom + 6);

      let left = anchorRect.left;
      left = Math.max(margin, Math.min(left, window.innerWidth - margin - width));

      return {
        position: 'fixed',
        left,
        top,
        width,
        zIndex: 9999,
      };
    })()
    : { position: 'fixed', right: 16, bottom: 80, zIndex: 9999 };

  return (
    <div
      ref={dropdownRef}
      style={style}
      className="bg-white dark:bg-gray-800 border-2 border-accent dark:border-accent rounded-lg shadow-2xl text-sm animate-fadeIn flex flex-col outline-none"
      tabIndex={-1}
      role="dialog"
      aria-label="Sélection de mentions"
    >
      <div className="flex-shrink-0 border-b dark:border-gray-700">
        <div className="flex items-center">
          {(canMentionMessages || canMentionTodos) && (
            <button
              type="button"
              onClick={() => {
                setActiveTab('content');
                onActiveIndexChange && onActiveIndexChange(0);
              }}
              className={`flex-1 px-4 py-2.5 text-xs font-semibold uppercase transition-colors ${canMentionUsers ? 'rounded-tl-md' : 'rounded-t-md'} ${
                activeTab === 'content'
                  ? 'bg-accent text-white dark:text-black border-b-2 border-accent'
                  : 'bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Messages & Todos ({contentItems.length})
            </button>
          )}
          {canMentionUsers && (
            <button
              type="button"
              onClick={() => {
                setActiveTab('users');
                onActiveIndexChange && onActiveIndexChange(0);
              }}
              className={`flex-1 px-4 py-2.5 text-xs font-semibold uppercase transition-colors rounded-tr-md ${
                activeTab === 'users'
                  ? 'bg-accent text-white dark:text-black border-b-2 border-accent'
                  : 'bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Utilisateurs ({userItems.length})
            </button>
          )}
        </div>
      </div>

      {displayItems.length === 0 ? (
        <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 text-xs">
          Aucun résultat dans cette catégorie
        </div>
      ) : (
        <ul
          ref={listRef}
          className="overflow-y-auto flex-1"
          style={{ maxHeight: '220px' }}
          role="listbox"
        >
          {displayItems.map((it, i) => {
            // 👇 Nettoyer le texte/titre avant affichage
            let displayText = '';
            if (it.kind === 'user') {
              displayText = it.name || '';
            } else if (it.kind === 'msg') {
              displayText = stripHtml(it.title) || '<sans titre>';
            } else if (it.kind === 'todo') {
              displayText = stripHtml(it.text) || '<sans texte>';
            }

            return (
              <li
                key={`${it.kind}-${it._id}`}
                ref={i === activeIndex ? activeItemRef : null}
                className={`px-4 py-3 cursor-pointer transition-colors ${
                  activeIndex === i
                    ? 'bg-accent/10 dark:bg-accent/20 border-l-4 border-accent'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 border-l-4 border-transparent'
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(it);
                }}
                onMouseEnter={() => onActiveIndexChange && onActiveIndexChange(i)}
                role="option"
                aria-selected={activeIndex === i}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`text-xs font-bold px-2 py-1 rounded flex-shrink-0 ${
                      it.kind === 'msg'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : it.kind === 'todo'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                    }`}
                  >
                    {it.kind === 'msg' ? 'MSG' : it.kind === 'todo' ? 'TODO' : 'USER'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-black dark:text-white truncate">
                      {displayText}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-mono truncate">
                      {it.kind === 'user' ? it.email : it._id}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="px-3 py-2 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 flex-shrink-0 rounded-b-md">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Tab: changer d'onglet • ↑↓ naviguer • ⏎ sélectionner • Esc fermer
        </p>
      </div>
    </div>
  );
};

export default SuggestionDropdown;